import { useRef, useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fileToResizedDataUrl } from "@/lib/image";

// Circular avatar with upload / remove. value = base64 data URL or null.
const ProfilePhotoInput = ({ value, onChange, fallback = "?", size = "h-20 w-20", testId = "photo-input" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file);
      onChange(dataUrl);
    } catch (err) {
      toast.error(err.message || "Could not process image.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className={`relative ${size} rounded-full overflow-hidden bg-gradient-to-br from-ark-sky to-ark-teal flex items-center justify-center shrink-0 shadow-ark-soft`}>
        {value ? (
          <img src={value} alt="Profile" className="h-full w-full object-cover" data-testid={`${testId}-preview`} />
        ) : (
          <span className="font-display font-bold text-2xl text-white">{fallback}</span>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="h-5 w-5 text-white animate-spin" />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          data-testid={`${testId}-upload`}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 text-sm font-semibold text-ark-skyDeep hover:text-ark-sky"
        >
          <Camera className="h-4 w-4" /> {value ? "Change photo" : "Upload photo"}
        </button>
        {value && (
          <button
            type="button"
            data-testid={`${testId}-remove`}
            onClick={() => onChange(null)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500 hover:text-red-600"
          >
            <X className="h-4 w-4" /> Remove
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testId}-file`} />
      </div>
    </div>
  );
};

export default ProfilePhotoInput;
